/**
 * PdkAddSystem — super_admin only.
 *
 * Wires a real PDK system to a property on shadow:
 *   1. Pick a property from /properties
 *   2. Enter PDK client_id, client_secret, panel_id (system_id)
 *   3. POST /admin-connector/integrations/pdk/enable  → connector-api persists + KMS-encrypts creds
 *   4. On success, verify round-trip by calling connector-api list_cloud_nodes
 *   5. Display discovered cloud nodes (proves creds + system_id work end-to-end)
 *
 * All PDK calls flow through shadow.api.intrlock.io.
 * This page NEVER calls PDK directly.
 */

import React, { useContext, useEffect, useState } from 'react';

const SHADOW_ADMIN_CONNECTOR = 'https://shadow.api.intrlock.io/admin-connector';
const SHADOW_CONNECTOR_ACTION = 'https://shadow.api.intrlock.io/connector/action';
const SHADOW_API = 'https://shadow.api.intrlock.io';

function getToken(): string {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem('intrlock_token') || window.localStorage.getItem('intrlock_jwt') || '';
}

function authHeaders(): HeadersInit {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

interface Property {
  id: string;
  name: string;
  address?: string;
}

interface CloudNode {
  id: string;
  name?: string;
  connectionStatus?: { connected: boolean };
  lastContactTime?: string;
}

export const PdkAddSystem: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [propsLoading, setPropsLoading] = useState(true);
  const [propsError, setPropsError] = useState('');

  // Form state
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [panelId, setPanelId] = useState('');

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [enableError, setEnableError] = useState('');
  const [enableSuccess, setEnableSuccess] = useState(false);

  // Cloud nodes discovered post-enable
  const [cloudNodes, setCloudNodes] = useState<CloudNode[]>([]);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [nodesError, setNodesError] = useState('');

  // Load properties
  useEffect(() => {
    setPropsLoading(true);
    fetch(`${SHADOW_API}/properties`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        setProperties(list);
        setPropsLoading(false);
      })
      .catch(e => {
        setPropsError(e.message || 'Failed to load properties');
        setPropsLoading(false);
      });
  }, []);

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPropertyId || !clientId || !clientSecret || !panelId) return;
    setSubmitting(true);
    setEnableError('');
    setEnableSuccess(false);
    setCloudNodes([]);
    setNodesError('');

    try {
      const res = await fetch(`${SHADOW_ADMIN_CONNECTOR}/integrations/pdk/enable`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          entity_type: 'property',
          entity_id: selectedPropertyId,
          credentials: {
            client_id: clientId,
            client_secret: clientSecret,
            panel_id: panelId,
            system_id: panelId, // also store alias for backward compat
          },
          settings: { system_id: panelId },
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      }
      setEnableSuccess(true);

      // Discover cloud nodes to prove round-trip works
      setNodesLoading(true);
      try {
        const nodeRes = await fetch(SHADOW_CONNECTOR_ACTION, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            integration_key: 'pdk',
            action: 'list_cloud_nodes',
            params: { panel_id: panelId },
          }),
        });
        const nodeData = await nodeRes.json();
        const nodes = (nodeData?.data && Array.isArray(nodeData.data))
          ? nodeData.data
          : (Array.isArray(nodeData) ? nodeData : []);
        setCloudNodes(nodes);
        if (!nodeRes.ok) {
          setNodesError(nodeData?.error || `Credentials saved but verify returned HTTP ${nodeRes.status}. Check client_id / client_secret.`);
        }
      } catch (err: any) {
        setNodesError(err.message || 'Could not verify credentials — check client_id / client_secret.');
      } finally {
        setNodesLoading(false);
      }
    } catch (err: any) {
      setEnableError(err.message || 'Failed to enable PDK integration');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 sm:px-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Add PDK System</h1>
        <p className="mt-1 text-sm text-gray-500">
          Wire a ProDataKey (PDK) access control system to a shadow property. All calls flow
          through connector-api — credentials are KMS-encrypted with the shadow vendor-tokens CMK.
        </p>
      </div>

      {/* Route + API info */}
      <div className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 font-mono">
        <div>POST shadow.api.intrlock.io/admin-connector/integrations/pdk/enable</div>
        <div className="mt-1 text-gray-400">KMS CMK: bfb91d97-8ec1-41d3-8c1c-adc802b0e36b (alias intrlock/shadow/vendor-tokens)</div>
        <div className="text-gray-400">EncryptionContext: &#123; service: 'intrlock-connector' &#125;</div>
      </div>

      <form onSubmit={handleEnable} className="space-y-5">
        {/* Property picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Property <span className="text-red-500">*</span>
          </label>
          {propsLoading ? (
            <div className="text-sm text-gray-400">Loading properties…</div>
          ) : propsError ? (
            <div className="text-sm text-red-500">{propsError}</div>
          ) : (
            <select
              value={selectedPropertyId}
              onChange={e => setSelectedPropertyId(e.target.value)}
              required
              className="block w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">-- Select a property --</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.address ? ` · ${p.address}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* PDK Client ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            PDK OAuth Client ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={clientId}
            onChange={e => setClientId(e.target.value)}
            required
            placeholder="From PDK developer portal"
            className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <p className="mt-1 text-xs text-gray-400">Found in PDK portal → Developer → OAuth Apps</p>
        </div>

        {/* PDK Client Secret */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            PDK OAuth Client Secret <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            value={clientSecret}
            onChange={e => setClientSecret(e.target.value)}
            required
            placeholder="PDK OAuth client secret"
            className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            autoComplete="new-password"
          />
          <p className="mt-1 text-xs text-gray-400">Stored encrypted via KMS — never logged or exposed</p>
        </div>

        {/* PDK Panel / System ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            PDK System ID (panel_id) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={panelId}
            onChange={e => setPanelId(e.target.value)}
            required
            placeholder="UUID from PDK portal (e.g. 3fa85f64-5717-4562-b3fc-2c963f66afa6)"
            className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <p className="mt-1 text-xs text-gray-400">PDK portal → Systems → select your system → copy UUID from URL</p>
        </div>

        {/* Error / success banners */}
        {enableError && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {enableError}
          </div>
        )}
        {enableSuccess && (
          <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-700">
            PDK integration enabled and credentials stored. Verifying round-trip to PDK…
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting || !selectedPropertyId || !clientId || !clientSecret || !panelId}
          className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Saving…' : 'Enable PDK Integration'}
        </button>
      </form>

      {/* Cloud nodes discovered after enable */}
      {enableSuccess && (
        <div className="mt-8">
          <h2 className="text-base font-semibold text-gray-900 mb-3">
            Discovered Cloud Nodes
            {nodesLoading && <span className="ml-2 text-sm text-gray-400 font-normal">Loading…</span>}
          </h2>

          {nodesError && (
            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800 mb-3">
              {nodesError}
            </div>
          )}

          {!nodesLoading && cloudNodes.length === 0 && !nodesError && (
            <p className="text-sm text-gray-500">
              No cloud nodes returned. Integration is configured — cloud nodes appear once
              PDK reports them for this system_id.
            </p>
          )}

          {cloudNodes.length > 0 && (
            <div className="space-y-2">
              {cloudNodes.map((n, i) => (
                <div
                  key={n.id || i}
                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg text-sm"
                >
                  <div>
                    <div className="font-medium text-gray-900">{n.name || 'Unnamed Node'}</div>
                    <div className="text-xs text-gray-400 font-mono mt-0.5">{n.id}</div>
                    {n.lastContactTime && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        Last contact: {new Date(n.lastContactTime).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      n.connectionStatus?.connected
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {n.connectionStatus?.connected ? 'Online' : 'Offline'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {enableSuccess && !nodesLoading && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <p className="font-semibold mb-1">Next steps:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Go to <strong>Devices</strong> to see live PDK device status for this property</li>
                <li>Use the <strong>Unlock</strong> button on any PDK device to trigger an admin unlock</li>
                <li>Tenants can see access history in the Tenant Portal under <strong>History</strong></li>
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PdkAddSystem;
